import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { z } from "zod";
import type { Database } from "@/integrations/supabase/types";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

/**
 * Public: record a single article view.
 *
 * Accepts nothing but a slug and returns nothing. All work happens in the
 * `record_article_view` security-definer function, so no table is publicly
 * writable and no data can be read back through this endpoint.
 */
export const recordArticleView = createServerFn({ method: "POST" })
  .inputValidator((data: { slug: string }) =>
    z.object({ slug: z.string().min(1).max(200) }).parse(data),
  )
  .handler(async ({ data }) => {
    const { getRequestHeader } = await import("@tanstack/react-start/server");
    const ua = (getRequestHeader("user-agent") ?? "").toLowerCase();
    // Crawlers execute JS in some cases (Googlebot, Bingbot preview renders);
    // their hits shouldn't shape editorial decisions.
    if (
      /bot|crawl|spider|slurp|headless|preview|monitor|lighthouse|pingdom|curl|wget|python-requests|facebookexternalhit|embedly|quora link preview/.test(
        ua,
      )
    ) {
      return { ok: true, counted: false as const };
    }

    const supabase = createClient<Database>(
      process.env.SUPABASE_URL!,
      process.env.SUPABASE_PUBLISHABLE_KEY!,
      { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
    );

    const { error } = await supabase.rpc("record_article_view", { _slug: data.slug });
    if (error) {
      // Never surface analytics failures to the reader.
      console.error("recordArticleView failed", error.message);
      return { ok: false, counted: false as const };
    }
    return { ok: true, counted: true as const };
  });

async function assertAdmin(supabase: any, userId: string) {
  const { data, error } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId)
    .eq("role", "admin")
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) throw new Error("Forbidden: admin role required");
}

/** Admin: aggregated view performance over the last N days. */
export const adminPerformanceSummary = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { days?: number } | undefined) =>
    z.object({ days: z.number().int().min(1).max(365).optional() }).parse(data ?? {}),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { buildPerformanceSummary } = await import("./analytics.server");
    return buildPerformanceSummary(context.supabase, data.days ?? 30);
  });

/**
 * Public: record a click on a site call-to-action (currently the Google
 * "preferred source" button). Counts only — no visitor data is stored.
 */
export const recordCtaClick = createServerFn({ method: "POST" })
  .inputValidator((data: { name: string; placement: string }) =>
    z
      .object({ name: z.string().min(1).max(60), placement: z.string().min(1).max(60) })
      .parse(data),
  )
  .handler(async ({ data }) => {
    const { getRequestHeader } = await import("@tanstack/react-start/server");
    const ua = (getRequestHeader("user-agent") ?? "").toLowerCase();
    if (/bot|crawl|spider|slurp|headless|preview|monitor|lighthouse|curl|wget/.test(ua)) {
      return { ok: true, counted: false as const };
    }
    try {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { error } = await supabaseAdmin.rpc("record_cta_click", {
        _name: data.name,
        _placement: data.placement,
      });
      if (error) throw new Error(error.message);
      return { ok: true, counted: true as const };
    } catch (err) {
      console.error("recordCtaClick failed", err instanceof Error ? err.message : err);
      return { ok: false, counted: false as const };
    }
  });
